import type {
  FacebookConnectionResponse,
  InstagramConnectionResponse,
  PlatformTypeName,
  WhatsAppConnectionResponse,
} from "@multi-chat/shared";
import { senderDisplayName } from "../campaigns/campaign-message-presentation.js";
import { channelCardTitle } from "../channels/channel-local-name.js";
import { formatInstagramUsername } from "../instagram/instagram-connection-presentation.js";

export type InboxChannelConnections = {
  whatsapp: WhatsAppConnectionResponse | null;
  facebook: FacebookConnectionResponse | null;
  instagram: InstagramConnectionResponse | null;
};

/**
 * Name of the connected account that owns the conversation's endpoint.
 * Null when that exact endpoint is not part of the current connections
 * (disconnected, replaced, or not loaded) — never another account's name.
 */
export function conversationConnectedAccountName(
  connections: InboxChannelConnections,
  platformType: PlatformTypeName,
  platformEndpointId: number,
): string | null {
  switch (platformType) {
    case "WHATSAPP": {
      const phone = connections.whatsapp?.phoneNumbers.find(
        (candidate) => candidate.platformEndpointId === platformEndpointId,
      );
      return phone ? senderDisplayName(phone) : null;
    }
    case "FACEBOOK": {
      const page = connections.facebook?.page;
      if (page == null || page.platformEndpointId !== platformEndpointId) return null;
      return channelCardTitle(page.localName, page.pageName);
    }
    case "INSTAGRAM": {
      const account = connections.instagram?.account;
      if (account == null || account.platformEndpointId !== platformEndpointId) return null;
      const providerTitle =
        account.name?.trim() || formatInstagramUsername(account.username) || account.username;
      return channelCardTitle(account.localName, providerTitle);
    }
    default: {
      const _exhaustive: never = platformType;
      return _exhaustive;
    }
  }
}
